import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getEmotionsInfo } from "../../../services/emotionsInfo";
import { Heart, Minus, ThumbsDown, PieChart as PieIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

const EmotionDashboard = () => {
  const { t } = useTranslation(["user"]);

  const accessToken = useSelector((state) => state.auth.accessToken);
  const [loading, setLoading] = useState(false);
  const [hasData, setHasData] = useState(true);
  const [data, setData] = useState([
    { key: "positive", value: 0, color: "#14B8A6" },
    { key: "neutral", value: 0, color: "#94A3B8" },
    { key: "negative", value: 0, color: "#FBBF24" },
  ]);

  useEffect(() => {
    const fetchEmotions = async () => {
      setLoading(true);
      setHasData(true);
      try {
        const res = await getEmotionsInfo(accessToken);
        if (!res || !res.ok || !res.data) {
          setHasData(false);
          return;
        }
        if (res?.ok) {
          let likes = Number(res.data.likes);
          let nulls = Number(res.data.nulls);
          let dislikes = Number(res.data.dislikes);

          if (likes === 0 && nulls === 0 && dislikes === 0) {
            nulls = 100;
          }

          setData([
            { key: "positive", value: likes, color: "#14B8A6" },
            { key: "neutral", value: nulls, color: "#94A3B8" },
            { key: "negative", value: dislikes, color: "#FBBF24" },
          ]);
        }
      } catch (error) {
        setHasData(false);
      } finally {
        setLoading(false);
      }
    };
    fetchEmotions();
  }, [accessToken]);

  /* ---------- label وسط هر بخش ---------- */
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#ffffff"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={14}
        fontWeight={800}
        style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.35))" }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  /* ---------- تیره‌کردن رنگ برای stroke ---------- */
  const darkerColor = (color) => {
    const shade = (c) => Math.max(0, Math.min(255, parseInt(c, 16) - 40));
    const r = shade(color.slice(1, 3));
    const g = shade(color.slice(3, 5));
    const b = shade(color.slice(5, 7));
    return `rgb(${r},${g},${b})`;
  };

  /* ---------- آیکون بر اساس نوع احساس ---------- */
  const iconFor = (key) => {
    if (key === "positive") return Heart;
    if (key === "negative") return ThumbsDown;
    return Minus;
  };

  return (
    <div className="relative w-full h-full flex flex-col overflow-hidden">

      {/* Header */}
      <div className="shrink-0 flex items-center gap-2 mb-3">
        <span
          className="
            shrink-0 flex h-8 w-8 items-center justify-center rounded-lg
            bg-gradient-to-br from-amber-300/30 to-teal-400/20
            border border-amber-300/40
          "
        >
          <PieIcon size={15} className="text-amber-400" />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-slate-800 dark:text-amber-100 truncate">
            {t("emotion.title")}
          </h3>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            {t("emotion.subtitle")}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="relative flex-1 min-h-0 flex flex-col">
        {loading ? (
          /* ---------- Skeleton ---------- */
          <div className="flex flex-col items-center justify-center w-full h-full gap-4 animate-pulse">
            <div className="relative h-48 w-48 sm:h-56 sm:w-56 rounded-full border-[18px] border-slate-200/70 dark:border-slate-700/50" />
            <div className="flex justify-around w-full max-w-md mt-2">
              {data.map((_, i) => (
                <div key={i} className="flex flex-col items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-slate-300/70 dark:bg-slate-700/70" />
                  <div className="w-12 h-3 bg-slate-300/70 dark:bg-slate-700/70 rounded" />
                </div>
              ))}
            </div>
          </div>
        ) : !hasData ? (
          /* ---------- Empty ---------- */
          <div className="flex flex-col items-center justify-center gap-3 m-auto text-slate-400 dark:text-slate-500">
            <div className="h-14 w-14 rounded-full border-2 border-dashed border-teal-400/40 flex items-center justify-center">
              <PieIcon size={22} className="animate-pulse text-teal-400/70" />
            </div>
            <p className="text-sm animate-pulse">{t("emotion.noData")}</p>
          </div>
        ) : (
          /* ---------- Chart ---------- */
          <div className="flex flex-col w-full h-full min-h-0 gap-2">
            <div className="flex-1 min-h-0 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <defs>
                    {data.map((entry, i) => (
                      <radialGradient
                        key={i}
                        id={`emotion-grad-${i}`}
                        cx="50%"
                        cy="50%"
                        r="75%"
                      >
                        <stop offset="0%" stopColor={entry.color} stopOpacity={0.95} />
                        <stop offset="100%" stopColor={darkerColor(entry.color)} stopOpacity={1} />
                      </radialGradient>
                    ))}
                  </defs>

                  <Pie
                    data={data.map((d) => ({ ...d, name: t(`emotion.${d.key}`) }))}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius="88%"
                    innerRadius="46%"
                    label={renderCustomizedLabel}
                    labelLine={false}
                    paddingAngle={3}
                    stroke="#ffffff"
                    strokeWidth={2}
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={`url(#emotion-grad-${index})`}
                        stroke={darkerColor(entry.color)}
                        strokeWidth={3}
                      />
                    ))}
                  </Pie>

                  <Tooltip
                    formatter={(value) => [
                      `${Number(value).toLocaleString("fa-IR")} ${t("emotion.post")}`,
                      "",
                    ]}
                    contentStyle={{
                      fontFamily: "Yekan, Vazir, sans-serif",
                      fontSize: 13,
                      borderRadius: 12,
                      background: "rgba(15, 23, 42, 0.92)",
                      border: "1px solid rgba(251, 191, 36, 0.4)",
                      boxShadow: "0 8px 30px -10px rgba(0,0,0,0.6)",
                      color: "#FCD34D",
                    }}
                    itemStyle={{ color: "#FCD34D", fontWeight: 700 }}
                    labelStyle={{ color: "#5EEAD4", fontSize: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Legend */}
            <div className="shrink-0 grid grid-cols-3 gap-2 w-full pt-1">
              {data.map((item, index) => {
                const Icon = iconFor(item.key);
                return (
                  <div
                    key={index}
                    className="
                      group flex items-center justify-center gap-1.5
                      px-2 py-1.5 rounded-lg
                      bg-white/50 dark:bg-white/5
                      border border-teal-500/15 dark:border-teal-400/15
                      backdrop-blur-md
                      transition-all duration-300
                      hover:scale-[1.03]
                      hover:border-amber-300/50
                      hover:shadow-[0_4px_16px_-6px_rgba(251,191,36,0.5)]
                    "
                  >
                    <span
                      className="shrink-0 flex h-5 w-5 items-center justify-center rounded-md"
                      style={{
                        background: `linear-gradient(135deg, ${item.color}, ${darkerColor(item.color)})`,
                        boxShadow: `0 2px 8px -2px ${item.color}80`,
                      }}
                    >
                      <Icon size={11} className="text-white" />
                    </span>
                    <span className="text-[11px] sm:text-xs font-bold text-slate-700 dark:text-slate-200 truncate">
                      {t(`emotion.${item.key}`)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmotionDashboard;